import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Building,
  FileText,
  Mail,
  RefreshCw,
  Search,
} from 'lucide-react'
import {
  useEmailInboxList,
  useEmailThreadDetail,
  useMailboxAccounts,
  useUpdateInboxItem,
  useUpdateSuggestedReply,
  type InboxListRow,
  type InboxStatusFilter,
  type SuggestedReply_,
} from 'src/api/db/apiEmailInbox'
import {
  useEmailInboxInsights,
  useEmailInboxSend,
  useEmailInboxStatus,
  useEmailInboxSync,
} from 'src/api/server/useEmailInbox'
import { paths, buildAdminOrderPath } from 'src/routes/paths'
import { PageContainer } from 'src/shared/components/PageContainer'
import { EmptyState } from 'src/shared/components/EmptyState'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { TabNavigation } from 'src/shared/components/TabNavigation'
import { Badge } from 'src/shared/components/ui/badge'
import { Button } from 'src/shared/components/ui/button'
import { Input } from 'src/shared/components/ui/input'
import { Textarea } from 'src/shared/components/ui/textarea'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { useDebounce } from 'src/shared/hooks/useDebounce'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/components/ui/utils'

const STATUS_TABS: Array<{
  id: InboxStatusFilter
  en: string
  sv: string
}> = [
  { id: 'needs_action', en: 'Needs action', sv: 'Behöver åtgärd' },
  { id: 'waiting', en: 'Waiting', sv: 'Väntar' },
  { id: 'done', en: 'Done', sv: 'Klart' },
  { id: 'ignored', en: 'Ignored', sv: 'Ignorerat' },
  { id: 'all', en: 'All', sv: 'Alla' },
]

const FUNCTION_CHIPS: Array<{
  id: string | null
  en: string
  sv: string
  variant: 'gray' | 'blue' | 'orange' | 'green' | 'purple'
}> = [
  { id: null, en: 'All', sv: 'Alla', variant: 'gray' },
  { id: 'sales', en: 'Sales', sv: 'Sälj', variant: 'orange' },
  { id: 'admin', en: 'Admin', sv: 'Admin', variant: 'blue' },
  { id: 'finance', en: 'Finance', sv: 'Finance', variant: 'purple' },
  { id: 'supplier', en: 'Supplier', sv: 'Leverantör', variant: 'green' },
]

const groupKey = (row: InboxListRow): string =>
  row.email_thread?.organization?.name ||
  row.email_thread?.email_contact?.domain ||
  row.email_thread?.email_contact?.email ||
  'Unassigned'

const formatWhen = (iso: string | null | undefined) => {
  if (!iso) return ''
  const date = new Date(iso)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const AdminEmailPage = () => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const [params, setParams] = useSearchParams()
  const [tab, setTab] = useState<'inbox' | 'insights'>('inbox')
  const [status, setStatus] = useState<InboxStatusFilter>('needs_action')
  const [functionTag, setFunctionTag] = useState<string | null>(null)
  const [mailboxEmail, setMailboxEmail] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 250)
  const selectedId = params.get('thread')

  const mailboxes = useMailboxAccounts()
  const list = useEmailInboxList({
    status,
    functionTag,
    search: debouncedSearch,
    mailboxEmail,
  })
  const statusQuery = useEmailInboxStatus()
  const sync = useEmailInboxSync()
  const insights = useEmailInboxInsights(tab === 'insights')

  const rows = list.data ?? []
  const grouped = useMemo(() => {
    const map = new Map<string, InboxListRow[]>()
    for (const row of rows) {
      const key = groupKey(row)
      const bucket = map.get(key) ?? []
      bucket.push(row)
      map.set(key, bucket)
    }
    return [...map.entries()]
  }, [rows])

  const setThread = (id: string | null) => {
    const next = new URLSearchParams(params)
    if (id) next.set('thread', id)
    else next.delete('thread')
    setParams(next, { replace: true })
  }

  if (list.isPending) return <FullScreenLoader />

  return (
    <PageContainer width="xl" className="gap-4">
      <Row className="items-start justify-between gap-4">
        <Row className="items-center gap-2">
          <Mail className="h-6 w-6" />
          <Column className="gap-0.5">
            <h1>{t('Inbox', 'Inkorg')}</h1>
            <p className="text-sm text-muted-foreground">
              {t(
                `${statusQuery.data?.needs_review ?? 0} need a reply · ${statusQuery.data?.accounts ?? 0} mailboxes`,
                `${statusQuery.data?.needs_review ?? 0} behöver svar · ${statusQuery.data?.accounts ?? 0} brevlådor`
              )}
            </p>
          </Column>
        </Row>
        <Row className="items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={sync.isPending}
            onClick={() =>
              sync.mutate(false, {
                onSuccess: (result) =>
                  toast({
                    variant: 'success',
                    title: t(
                      `Synced ${result.ingested} new emails`,
                      `Synkade ${result.ingested} nya mejl`
                    ),
                  }),
                onError: (error: Error) =>
                  toast({
                    variant: 'destructive',
                    title: error.message,
                  }),
              })
            }
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', sync.isPending && 'animate-spin')}
            />
            {t('Sync', 'Synka')}
          </Button>
        </Row>
      </Row>

      <TabNavigation
        tabs={[
          { id: 'inbox', label: t('Inbox', 'Inkorg') },
          { id: 'insights', label: t('Insights', 'Insikter') },
        ]}
        activeTab={tab}
        onTabChange={(id) => setTab(id as 'inbox' | 'insights')}
      />

      {tab === 'insights' ? (
        <InsightsPanel
          loading={insights.isPending}
          data={insights.data}
        />
      ) : (
        <Column className="min-h-0 flex-1 gap-3">
          <Row className="flex-wrap items-center gap-2">
            {STATUS_TABS.map((item) => (
              <Badge
                key={item.id}
                clickable
                pressed={status === item.id}
                variant={status === item.id ? 'orange' : 'gray'}
                onClick={() => setStatus(item.id)}
              >
                {t(item.en, item.sv)}
              </Badge>
            ))}
          </Row>
          <Row className="flex-wrap items-center gap-2">
            {FUNCTION_CHIPS.map((chip) => (
              <Badge
                key={chip.id ?? 'all'}
                clickable
                pressed={functionTag === chip.id}
                variant={functionTag === chip.id ? chip.variant : 'gray'}
                onClick={() => setFunctionTag(chip.id)}
              >
                {t(chip.en, chip.sv)}
              </Badge>
            ))}
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={mailboxEmail ?? ''}
              onChange={(event) =>
                setMailboxEmail(event.target.value || null)
              }
            >
              <option value="">
                {t('All mailboxes', 'Alla brevlådor')}
              </option>
              {(mailboxes.data ?? []).map((box) => (
                <option key={box.id} value={box.email}>
                  {box.display_name
                    ? `${box.display_name} (${box.email})`
                    : box.email}
                </option>
              ))}
            </select>
            <div className="relative min-w-[12rem] flex-1">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-7"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('Search customer or subject', 'Sök kund eller ämne')}
              />
            </div>
          </Row>

          <Row className="min-h-[32rem] flex-1 overflow-hidden rounded-[var(--radius-outer)] border border-[hsl(var(--border-subtle))] bg-[hsl(var(--surface-panel)/0.7)]">
            <Column className="w-[22rem] shrink-0 overflow-y-auto border-r border-[hsl(var(--border-subtle))]">
              {grouped.length === 0 ? (
                <div className="p-6">
                  <EmptyState
                    withCard={false}
                    icon={<Mail className="h-5 w-5" />}
                    iconColor="blue"
                    title={t('No emails yet', 'Inga mejl än')}
                    description={t(
                      'Sync Instantly to pull conversations into this inbox.',
                      'Synka Instantly för att hämta konversationer hit.'
                    )}
                  />
                </div>
              ) : (
                grouped.map(([group, items]) => (
                  <Column key={group} className="border-b border-[hsl(var(--border-subtle))]">
                    <p className="px-3 py-2 text-xs font-medium text-muted-foreground">
                      {group}
                    </p>
                    {items.map((row) => {
                      const thread = row.email_thread
                      const active = thread?.id === selectedId
                      const draft = (row.suggested_reply ?? []).find(
                        (item) =>
                          item.status === 'draft' || item.status === 'edited'
                      )
                      return (
                        <button
                          key={row.id}
                          type="button"
                          onClick={() => setThread(thread?.id ?? null)}
                          className={cn(
                            'w-full px-3 py-2.5 text-left transition-colors',
                            active
                              ? 'bg-orange-50'
                              : 'hover:bg-black/[0.03]'
                          )}
                        >
                          <Row className="items-start justify-between gap-2">
                            <p className="line-clamp-1 text-sm font-medium">
                              {thread?.subject || t('No subject', 'Inget ämne')}
                            </p>
                            <span className="shrink-0 text-[10px] text-muted-foreground">
                              {formatWhen(thread?.last_message_at)}
                            </span>
                          </Row>
                          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                            {row.summary || thread?.snippet}
                          </p>
                          {draft?.body_text && (
                            <p className="mt-1 line-clamp-2 text-xs text-orange-800">
                              {draft.body_text}
                            </p>
                          )}
                          <Row className="mt-1 gap-1">
                            <Badge size="sm" variant="gray">
                              {row.function_tag}
                            </Badge>
                            <Badge size="sm" variant="blue">
                              {row.intent_tag}
                            </Badge>
                            {draft && (
                              <Badge size="sm" variant="orange">
                                {t('Ready to send', 'Redo att skicka')}
                              </Badge>
                            )}
                          </Row>
                        </button>
                      )
                    })}
                  </Column>
                ))
              )}
            </Column>
            <Column className="min-w-0 flex-1 overflow-y-auto p-4">
              {selectedId ? (
                <ThreadPane threadId={selectedId} />
              ) : (
                <EmptyState
                  withCard={false}
                  icon={<Mail className="h-5 w-5" />}
                  title={t('Select a conversation', 'Välj en konversation')}
                  description={t(
                    'Suggested replies appear here when something needs you.',
                    'Förslag på svar visas här när något behöver dig.'
                  )}
                />
              )}
            </Column>
          </Row>
        </Column>
      )}
    </PageContainer>
  )
}

const InsightsPanel = ({
  loading,
  data,
}: {
  loading: boolean
  data:
    | {
        needs_review: number
        waiting: number
        waiting_over_24h: number
        avg_minutes_to_draft: number | null
        avg_minutes_to_send: number | null
        sent_7d: number
      }
    | undefined
}) => {
  const { t } = useLocalization()
  if (loading) return <FullScreenLoader />
  const cards = [
    {
      label: t('Needs review', 'Behöver granskning'),
      value: data?.needs_review ?? 0,
    },
    {
      label: t('Waiting on them', 'Väntar på motpart'),
      value: data?.waiting ?? 0,
    },
    {
      label: t('Waiting over 24h', 'Väntat över 24 tim'),
      value: data?.waiting_over_24h ?? 0,
    },
    {
      label: t('Avg minutes to draft', 'Snitt minuter till utkast'),
      value: data?.avg_minutes_to_draft ?? '—',
    },
    {
      label: t('Avg minutes to send', 'Snitt minuter till skickat'),
      value: data?.avg_minutes_to_send ?? '—',
    },
    {
      label: t('Sent last 7 days', 'Skickade senaste 7 dagarna'),
      value: data?.sent_7d ?? 0,
    },
  ]
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {cards.map((card) => (
        <Column
          key={card.label}
          className="gap-1 rounded-[var(--radius-outer)] border border-[hsl(var(--border-subtle))] bg-[hsl(var(--surface-panel))] p-4"
        >
          <p className="text-xs text-muted-foreground">{card.label}</p>
          <p className="text-2xl font-medium">{card.value}</p>
        </Column>
      ))}
    </div>
  )
}

const ThreadPane = ({ threadId }: { threadId: string }) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const detail = useEmailThreadDetail(threadId)
  const send = useEmailInboxSend()
  const updateItem = useUpdateInboxItem()
  const updateDraft = useUpdateSuggestedReply()
  const [bodies, setBodies] = useState<Record<string, string>>({})
  const [showThread, setShowThread] = useState(false)

  useEffect(() => {
    const drafts = detail.data?.drafts
    if (!drafts) return
    setBodies((current) => {
      const next = { ...current }
      for (const draft of drafts) {
        if (next[draft.id] == null) next[draft.id] = draft.body_text
      }
      return next
    })
  }, [detail.data?.drafts])

  if (detail.isPending) {
    return <p className="text-sm text-muted-foreground">{t('Loading', 'Laddar')}</p>
  }
  if (!detail.data) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('Thread not found', 'Tråden hittades inte')}
      </p>
    )
  }

  const { thread, messages, item, drafts } = detail.data
  const org = thread.organization
  const quote = thread.quote
  const activeDrafts = drafts.filter(
    (draft) => draft.status === 'draft' || draft.status === 'edited'
  )
  const lastInbound = [...messages]
    .reverse()
    .find((message) => message.direction === 'inbound')
  const lastMessage = messages[messages.length - 1]

  const onSend = (draft: SuggestedReply_) => {
    send.mutate(
      {
        suggested_reply_id: draft.id,
        body_text: bodies[draft.id] ?? draft.body_text,
      },
      {
        onSuccess: () =>
          toast({
            variant: 'success',
            title: t('Sent', 'Skickat'),
          }),
        onError: (error: Error) =>
          toast({
            variant: 'destructive',
            title: error.message,
          }),
      }
    )
  }

  return (
    <Column className="gap-4">
      {activeDrafts.map((draft) => (
        <Column
          key={draft.id}
          className="gap-2 rounded-[var(--radius-outer)] border border-orange-200 bg-orange-50/50 p-3"
        >
          <Row className="items-center justify-between">
            <p className="text-sm font-medium">
              {draft.kind === 'supplier'
                ? t('Supplier draft', 'Leverantörsutkast')
                : t('Reply', 'Svar')}
            </p>
            <span className="text-xs text-muted-foreground">{draft.to_email}</span>
          </Row>
          <Textarea
            noStyle
            className="min-h-[10rem] bg-white"
            autoFocus
            value={bodies[draft.id] ?? draft.body_text}
            onChange={(event) =>
              setBodies((current) => ({
                ...current,
                [draft.id]: event.target.value,
              }))
            }
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault()
                onSend(draft)
              }
            }}
            onBlur={() => {
              const body = bodies[draft.id]
              if (body && body !== draft.body_text) {
                updateDraft.mutate({
                  id: draft.id,
                  body_text: body,
                  status: 'edited',
                })
              }
            }}
          />
          <Row className="items-center gap-2">
            <Button
              variant="primaryOrange"
              size="sm"
              disabled={send.isPending}
              onClick={() => onSend(draft)}
            >
              {t('Send', 'Skicka')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                updateDraft.mutate({
                  id: draft.id,
                  status: 'discarded',
                })
              }
            >
              {t('Discard', 'Kasta')}
            </Button>
            <span className="text-[11px] text-muted-foreground">
              {t('⌘ Enter to send', '⌘ Enter för att skicka')}
            </span>
          </Row>
        </Column>
      ))}

      {item && (
        <Row className="gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              updateItem.mutate({ id: item.id, status: 'ignored' })
            }
          >
            {t('Ignore', 'Ignorera')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => updateItem.mutate({ id: item.id, status: 'done' })}
          >
            {t('Mark done', 'Markera klar')}
          </Button>
        </Row>
      )}

      <Column className="gap-1">
        <h2>{thread.subject || t('No subject', 'Inget ämne')}</h2>
        <Row className="flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>{thread.email_contact?.email}</span>
          {org && (
            <Link
              to={`${paths.adminOrganizations}/${org.id}`}
              className="inline-flex items-center gap-1 text-foreground"
            >
              <Building className="h-3.5 w-3.5" />
              {org.name}
            </Link>
          )}
          {quote?.public_id && (
            <Link
              to={buildAdminOrderPath(quote.public_id)}
              className="inline-flex items-center gap-1 text-foreground"
            >
              <FileText className="h-3.5 w-3.5" />
              #{quote.public_id}
            </Link>
          )}
        </Row>
      </Column>

      <Column className="gap-3">
        {(showThread ? messages : lastInbound ? [lastInbound] : lastMessage ? [lastMessage] : []).map(
          (message) => (
            <Column
              key={message.id}
              className={cn(
                'max-w-[42rem] gap-1 rounded-xl border px-3 py-2 text-sm',
                message.direction === 'outbound'
                  ? 'ml-auto border-orange-200 bg-orange-50/70'
                  : 'border-[hsl(var(--border-subtle))] bg-white'
              )}
            >
              <Row className="justify-between gap-2 text-xs text-muted-foreground">
                <span>{message.from_email}</span>
                <span>{formatWhen(message.sent_at)}</span>
              </Row>
              <p className="whitespace-pre-wrap">
                {message.body_text || t('(no text)', '(ingen text)')}
              </p>
            </Column>
          )
        )}
        {messages.length > 1 && (
          <button
            type="button"
            className="self-start text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => setShowThread((open) => !open)}
          >
            {showThread
              ? t('Hide earlier messages', 'Dölj tidigare meddelanden')
              : t(
                  `Show ${messages.length - 1} earlier messages`,
                  `Visa ${messages.length - 1} tidigare meddelanden`
                )}
          </button>
        )}
      </Column>
    </Column>
  )
}
